import { createSlice, createDraftSafeSelector, PayloadAction } from '@reduxjs/toolkit';

interface IFacilityInfo {
  facilityId: string;
}

interface FacilityState {
  facilityList: IFacilityInfo[];
  facilityInfo: IFacilityInfo;
  actionResult: string;
  isLoading: boolean;
  error: string | null;
}

const InitialState: FacilityState = {
  facilityList: [],
  facilityInfo: {},
  actionResult: '',
  isLoading: false,
  error: '',
};

const reducers = {
  stateClear: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.facilityList = [];
    state.facilityInfo = {};
    state.actionResult = '';
    state.isLoading = false;
    state.error = '';
  },
  list: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = true;
    state.actionResult = 'LIST_REQ';
    state.error = '';
  },
  listSuccess: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.facilityList = payload.facilityList;
    state.isLoading = false;
    state.actionResult = 'LIST_OK';
    state.error = '';
  },
  listFailure: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = false;
    state.actionResult = 'LIST_ERR';
    state.error = payload.error;
  },
  detail: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = true;
    state.actionResult = 'DETAIL_REQ';
    state.error = '';
  },
  detailSuccess: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.facilityInfo = payload.facilityInfo;
    state.isLoading = false;
    state.actionResult = 'DETAIL_OK';
    state.error = '';
  },
  detailFailure: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = false;
    state.actionResult = 'DETAIL_ERR';
    state.error = payload.error;
  },
  add: (state: FacilityState, { payload }: PayloadAction<CsutomerFacilityState>) => {
    state.isLoading = true;
    state.actionResult = 'ADD_REQ';
    state.error = '';
  },
  addSuccess: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = false;
    state.actionResult = 'ADD_OK';
    state.error = '';
  },
  addFailure: (state: FacilityState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'ADD_ERR';
    state.error = action.payload;
  },
  edit: (state: FacilityState, { payload }: PayloadAction<CsutomerFacilityState>) => {
    state.isLoading = true;
    state.actionResult = 'EDIT_REQ';
    state.error = '';
  },
  editSuccess: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = false;
    state.actionResult = 'EDIT_OK';
    state.error = '';
  },
  editFailure: (state: FacilityState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'EDIT_ERR';
    state.error = action.payload;
  },
  remove: (state: FacilityState, { payload }: PayloadAction<CsutomerFacilityState>) => {
    state.isLoading = true;
    state.actionResult = 'REMOVE_REQ';
    state.error = '';
  },
  removeSuccess: (state: FacilityState, { payload }: PayloadAction<FacilityState>) => {
    state.isLoading = false;
    state.actionResult = 'REMOVE_OK';
    state.error = '';
  },
  removeFailure: (state: FacilityState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'REMOVE_ERR';
    state.error = action.payload;
  },
  actionResultClear: (state: FacilityState) => {
    state.actionResult = '';
  },
};

const slice = createSlice({
  name: 'facility',
  initialState: InitialState,
  reducers: reducers,
});

const selectfacilityList = createDraftSafeSelector(
  (state: FacilityState) => state.facilityList,
  facilityList => facilityList,
);

const selectfacilityInfo = createDraftSafeSelector(
  (state: FacilityState) => state.facilityInfo,
  facilityInfo => facilityInfo,
);

const selectStatus = createDraftSafeSelector(
  (state: FacilityState) => state.isLogin,
  (state: FacilityState) => state.actionResult,
  (state: FacilityState) => state.isLoading,
  (state: FacilityState) => state.error,
  (isLogin, actionResult, isLoading, error) => ({
    isLogin,
    actionResult,
    isLoading,
    error,
  }),
);

export const facilitySelector = {
  facilityList: state => selectfacilityList(state[FACILITY]),
  facilityInfo: state => selectfacilityInfo(state[FACILITY]),
  status: state => selectStatus(state[FACILITY]),
};

export const FACILITY = slice.name;
export const facilityReducer = slice.reducer;
export const facilityAction = slice.actions;
